import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Link2, Plus, Copy, Check, ExternalLink, Edit, Trash2, 
  Save, X, Eye, EyeOff, Search, Filter, MoreVertical, Globe,
  Briefcase, Key, User, Lock, ChevronDown, ArrowUpRight
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth, api } from '../lib/auth';

const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// Partnership Card Component
function PartnershipCard({ partnership, onEdit, onDelete, onCopyLink }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleCopy = () => {
    onCopyLink(partnership.affiliate_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-cyan-500/30 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-lg">{partnership.company_name}</h3>
            <p className="text-sm text-gray-400">
              Added {new Date(partnership.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMenu(!showMenu)}
            className="text-gray-400 hover:text-white"
          >
            <MoreVertical className="w-5 h-5" />
          </Button>
          
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-[#1a2235] border border-white/10 rounded-lg shadow-xl z-10 py-1 min-w-[120px]">
              <button
                onClick={() => { onEdit(partnership); setShowMenu(false); }}
                className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" /> Edit
              </button>
              <button
                onClick={() => { onDelete(partnership.id); setShowMenu(false); }}
                className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Login Info */}
      {(partnership.dashboard_url || partnership.login_email) && (
        <div className="mb-4 p-3 bg-black/20 rounded-lg space-y-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Dashboard Access</p>
          
          {partnership.dashboard_url && (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-500" />
              <a 
                href={partnership.dashboard_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-cyan-400 hover:underline truncate flex-1"
              >
                {partnership.dashboard_url}
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => window.open(partnership.dashboard_url, '_blank')}
              >
                <ExternalLink className="w-3 h-3 text-gray-400" />
              </Button>
            </div>
          )}
          
          {partnership.login_email && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-300">{partnership.login_email}</span>
            </div>
          )}
          
          {partnership.login_password && (
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-300 font-mono">
                {showPassword ? partnership.login_password : '••••••••••'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-3 h-3 text-gray-400" />
                ) : (
                  <Eye className="w-3 h-3 text-gray-400" />
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Affiliate Link */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Affiliate Link</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2 bg-black/30 rounded-lg text-sm text-cyan-400 truncate font-mono">
            {partnership.affiliate_link}
          </div>
          <Button
            size="sm"
            onClick={handleCopy}
            className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            onClick={() => window.open(partnership.affiliate_link, '_blank')}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Notes */}
      {partnership.notes && (
        <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <p className="text-xs text-amber-400 mb-1">Notes</p>
          <p className="text-sm text-gray-300">{partnership.notes}</p>
        </div>
      )}
    </motion.div>
  );
}

// Add/Edit Partnership Modal
function PartnershipModal({ isOpen, onClose, onSave, editingPartnership }) {
  // Use a key-based reset approach instead of useEffect
  const getInitialFormData = () => ({
    company_name: editingPartnership?.company_name || '',
    dashboard_url: editingPartnership?.dashboard_url || '',
    login_email: editingPartnership?.login_email || '',
    login_password: editingPartnership?.login_password || '',
    affiliate_link: editingPartnership?.affiliate_link || '',
    notes: editingPartnership?.notes || ''
  });
  
  const [formData, setFormData] = useState(getInitialFormData);
  const [saving, setSaving] = useState(false);

  // Reset form data when modal opens or editing partnership changes
  const resetKey = `${isOpen}-${editingPartnership?.id || 'new'}`;
  useEffect(() => {
    if (isOpen) {
      const newData = getInitialFormData();
      setFormData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.affiliate_link) {
      toast.error('Company name and affiliate link are required');
      return;
    }
    setSaving(true);
    await onSave(formData, editingPartnership?.id);
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1525] border border-white/10 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {editingPartnership ? 'Edit Partnership' : 'Add New Partnership'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5 text-gray-400" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-gray-300">Company Name *</Label>
            <Input
              value={formData.company_name}
              onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="e.g., GoHighLevel, ClickFunnels, etc."
              className="bg-white/5 border-white/10 text-white mt-1"
              required
            />
          </div>

          <div className="pt-4 border-t border-white/10">
            <p className="text-sm text-gray-400 mb-3">Dashboard Access (optional)</p>
            
            <div className="space-y-3">
              <div>
                <Label className="text-gray-400 text-sm">Dashboard URL</Label>
                <Input
                  value={formData.dashboard_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, dashboard_url: e.target.value }))}
                  placeholder="https://app.gohighlevel.com/dashboard"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-400 text-sm">Login Email/Username</Label>
                  <Input
                    value={formData.login_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, login_email: e.target.value }))}
                    placeholder="your@email.com"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Password</Label>
                  <Input
                    type="password"
                    value={formData.login_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, login_password: e.target.value }))}
                    placeholder="••••••••"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <Label className="text-gray-300">Affiliate Link *</Label>
            <Input
              value={formData.affiliate_link}
              onChange={(e) => setFormData(prev => ({ ...prev, affiliate_link: e.target.value }))}
              placeholder="https://www.gohighlevel.com/?fp_ref=yourcode"
              className="bg-white/5 border-white/10 text-white mt-1"
              required
            />
          </div>

          <div>
            <Label className="text-gray-400 text-sm">Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Commission rates, payout schedule, special terms..."
              className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/20 text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              {saving ? 'Saving...' : (editingPartnership ? 'Update' : 'Add Partnership')}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function AffiliateDashboardPage() {
  const { user } = useAuth();
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadPartnerships();
  }, []);

  const loadPartnerships = async () => {
    try {
      const response = await api.get('/api/affiliate/partnerships');
      setPartnerships(response.data.partnerships || []);
    } catch (error) {
      console.error('Failed to load partnerships:', error);
      // If endpoint doesn't exist yet, start with empty array
      setPartnerships([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData, partnershipId) => {
    try {
      if (partnershipId) {
        // Update existing
        await api.put(`/api/affiliate/partnerships/${partnershipId}`, formData);
        toast.success('Partnership updated!');
      } else {
        // Create new
        await api.post('/api/affiliate/partnerships', formData);
        toast.success('Partnership added!');
      }
      loadPartnerships();
      setShowModal(false);
      setEditingPartnership(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save partnership');
    }
  };

  const handleDelete = async (partnershipId) => {
    if (!confirm('Are you sure you want to delete this partnership?')) return;
    
    try {
      await api.delete(`/api/affiliate/partnerships/${partnershipId}`);
      toast.success('Partnership deleted');
      loadPartnerships();
    } catch (error) {
      toast.error('Failed to delete partnership');
    }
  };

  const handleEdit = (partnership) => {
    setEditingPartnership(partnership);
    setShowModal(true);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const filteredPartnerships = partnerships.filter(p =>
    p.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 pt-20 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Affiliate Partnerships</h1>
              <p className="text-gray-400">Manage your affiliate programs and referral links</p>
            </div>
          </div>
          <Button
            onClick={() => { setEditingPartnership(null); setShowModal(true); }}
            className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Partnership
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">Find Affiliate Programs</h3>
              <p className="text-sm text-gray-400">
                Browse businesses in our directory to find affiliate programs to join. Each company has their own signup process.
              </p>
              <a 
                href={EXTERNAL_DIRECTORY_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 mt-2"
              >
                Explore Directory <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Search */}
        {partnerships.length > 0 && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search partnerships..."
                className="pl-10 bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
        )}

        {/* Partnerships Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading partnerships...</p>
          </div>
        ) : filteredPartnerships.length === 0 ? (
          <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
            <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? 'No partnerships found' : 'No Affiliate Partnerships Yet'}
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? 'Try a different search term'
                : 'Start adding the affiliate programs you promote. Keep track of your login info and referral links all in one place.'
              }
            </p>
            {!searchQuery && (
              <Button
                onClick={() => { setEditingPartnership(null); setShowModal(true); }}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Your First Partnership
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredPartnerships.map((partnership) => (
              <PartnershipCard
                key={partnership.id}
                partnership={partnership}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCopyLink={handleCopyLink}
              />
            ))}
          </div>
        )}

        {/* Stats Footer */}
        {partnerships.length > 0 && (
          <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <p className="text-gray-400">
                <span className="text-white font-semibold">{partnerships.length}</span> affiliate partnership{partnerships.length !== 1 ? 's' : ''} tracked
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditingPartnership(null); setShowModal(true); }}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-1" /> Add More
              </Button>
            </div>
          </div>
        )}

        {/* Modal */}
        <PartnershipModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingPartnership(null); }}
          onSave={handleSave}
          editingPartnership={editingPartnership}
        />
      </div>
    </DashboardLayout>
  );
}
